import React from 'react';
import { GraduationCap, LogIn, FileText, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { useSchoolPublicInfo } from '../../internal-owned/school-settings-v2/hooks/useSchoolPublicInfo';
interface Props {
  onSignIn: () => void;
  onSignUp: () => void;
  onNavigatePrivacy: () => void;
  onNavigateTerms: () => void;
}

export const HomePage: React.FC<Props> = ({ onSignIn, onSignUp, onNavigatePrivacy, onNavigateTerms }) => {
  const {
    schoolName,
    schoolLogo,
    academicYearLabel,
    applicationOpenDateLabel,
    onTimeDeadlineLabel,
    hardCutoffDeadlineLabel,
    lateFeeLabel,
    fees,
    tuitionRates,
    contactInfo,
    isLoading,
  } = useSchoolPublicInfo({ readMode: 'public' });

  const requiredFees = fees.slice(0, 6);

  return (
    <div
      className="min-h-screen flex flex-col bg-background-light font-sans text-irongrey selection:bg-primary selection:text-white"
      data-extracted-from="owned:ystnj/public/home-root"
    >
      {/* Header */}
      <header
        className="bg-white border-b border-gray-100 sticky top-0 z-50"
        data-extracted-from="owned:ystnj/public/home-header"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 flex items-center justify-center">
              {schoolLogo ? (
                <img
                  src={schoolLogo}
                  alt={`${schoolName} logo`}
                  data-testid="public-apply-school-logo"
                  className="h-8 w-8 rounded-md object-contain"
                />
              ) : (
                <GraduationCap className="text-[#237062] w-6 h-6" />
              )}
            </div>
            <h1 className="font-bold text-irongrey text-lg tracking-tight" data-testid="public-apply-school-name">{schoolName}</h1>
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={onSignIn}
              className="flex w-[105px] items-center justify-center gap-2 bg-[#f0fdf9] hover:bg-[#e6fcf5] text-[#28e2a7] px-4 py-2 rounded-lg font-semibold transition-colors"
            >
              <LogIn className="w-4 h-4" />
              <span className="text-sm">Sign in</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main
        className="flex-1 w-full max-w-7xl mx-auto px-6 md:px-12 py-10 bg-background-light"
        data-extracted-from="owned:ystnj/public/home-main"
      >
        <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden divide-y divide-gray-100 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.04)]">
          
          {/* Apply Here */}
          <section className="p-8" data-extracted-from="owned:ystnj/public/home-apply-section">
            <h3 className="font-bold text-xs text-irongrey/60 mb-6 uppercase tracking-widest">Apply Here!</h3>
            <div className="bg-teal-50 rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border border-teal-100/50">
              <div className="flex items-start gap-5">
                <div className="bg-teal-100/50 p-3 rounded-lg text-[#3f8066]">
                  <FileText className="w-8 h-8 opacity-80" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-irongrey mb-1" data-testid="public-apply-academic-year">Apply for the {academicYearLabel} academic year</h2>
                  <p className="text-irongrey/80 text-sm">The deadline for on-time submission is <span className="font-semibold text-irongrey" data-testid="public-apply-on-time-deadline">{onTimeDeadlineLabel}</span>.</p>
                </div>
              </div>
              <button 
                onClick={onSignUp}
                className="w-full sm:w-auto bg-[#237062] hover:bg-[#1b554a] text-white font-semibold py-2.5 px-6 rounded-lg transition-colors shadow-sm whitespace-nowrap text-center"
              >
                Begin Application
              </button>
            </div>
          </section>

          {/* Important Dates */}
          <section className="p-8 pb-[57px]" data-extracted-from="owned:ystnj/public/home-dates-section"> 
            <h3 className="font-bold text-xs text-irongrey/60 mb-6 uppercase tracking-widest">Important Dates</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="flex gap-4">
                <div className="mt-0.5">
                  <div className="bg-[#28e2a7]/10 p-2 rounded-full text-[#28e2a7]">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-irongrey text-base" data-testid="public-apply-open-date">{applicationOpenDateLabel}</h4>
                  <p className="text-xs text-irongrey/60 font-medium mt-0.5 uppercase tracking-wide">Application Opens</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="mt-0.5">
                  <div className="bg-orange-50 p-2 rounded-full text-orange-400">
                    <Clock className="w-5 h-5" />
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-irongrey text-base">{onTimeDeadlineLabel}</h4>
                  <p className="text-xs text-irongrey/60 font-medium mt-0.5 uppercase tracking-wide">On-Time Submission Deadline</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="mt-0.5">
                  <div className="bg-red-50 p-2 rounded-full text-red-400">
                    <AlertTriangle className="w-5 h-5" />
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-irongrey text-base" data-testid="public-apply-decision-date">{hardCutoffDeadlineLabel}</h4>
                  <p className="text-xs text-irongrey/60 font-medium mt-0.5 uppercase tracking-wide">Hard Cutoff Deadline ({lateFeeLabel} Late Fee)</p>
                </div>
              </div>
            </div>
          </section>

          {/* Info Grid */}
          <section
            className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-gray-100"
            data-extracted-from="owned:ystnj/public/home-info-grid"
          >
            {/* Need Help */}
            <div className="p-8 pb-12 flex flex-col">
              <h3 className="font-bold text-xs text-irongrey/60 mb-6 uppercase tracking-widest">Need Help?</h3>
              <div className="flex flex-col h-full justify-between">
                <p className="text-irongrey text-sm leading-relaxed mb-8">
                  Please contact the business office if you have questions about the application process or required documents.
                </p>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] uppercase font-bold text-irongrey/40 tracking-wider">Email</span>
                    <span className="text-sm font-semibold text-irongrey hover:text-primary transition-colors cursor-pointer">{contactInfo.email}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] uppercase font-bold text-irongrey/40 tracking-wider">Phone</span>
                    <span className="text-sm font-semibold text-irongrey">{contactInfo.phone}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] uppercase font-bold text-irongrey/40 tracking-wider">Hours</span>
                    <span className="text-sm font-semibold text-irongrey">{contactInfo.hours}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Policies */}
            <div className="p-8 flex flex-col bg-red-50/5">
              <div className="flex items-center gap-3 mb-6">
                <h3 className="font-bold text-xs text-irongrey/60 uppercase tracking-widest">Policies</h3>
              </div>
              <div className="flex-1">
                <p className="text-sm text-irongrey leading-relaxed">
                  No child will be permitted into the program without full proof of <span className="font-bold">immunization schedule</span>. Religious exemptions are not accepted per school board policy.
                </p>
              </div>
            </div>

            {/* Required Fees */}
            <div className="p-8 flex flex-col">
              <div className="flex items-center gap-3 mb-6">
                <h3 className="font-bold text-xs text-irongrey/60 uppercase tracking-widest">Required Fees</h3>
              </div>
              <div className="space-y-4">
                {requiredFees.length > 0 ? requiredFees.map((fee, index) => (
                  <div key={fee.id} data-testid="public-apply-fee-row" className={`flex justify-between items-center ${index < requiredFees.length - 1 ? 'border-b border-gray-50 pb-2' : ''}`}>
                    <span className="text-sm font-medium text-irongrey">{fee.name}:</span>
                    <span className="text-sm font-bold text-irongrey">{fee.amountLabel}</span>
                  </div>
                )) : (
                  <p className="text-sm text-irongrey/70">No fee schedule configured.</p>
                )}
              </div>
            </div>
          </section>

          {/* Annual Tuition Rates */}
          <section className="overflow-hidden" data-extracted-from="owned:ystnj/public/home-tuition-section">
            <div className="p-8 flex items-center gap-3 bg-gray-50/30">
              <h3 className="text-irongrey font-bold text-lg">Annual Tuition Rates</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/30">
                    <th className="py-4 px-8 text-xs font-bold text-irongrey uppercase tracking-widest">Grade Level</th>
                    <th className="py-4 px-8 text-xs font-bold text-irongrey uppercase tracking-widest text-right">Tuition</th>
                  </tr>
                </thead>
                <tbody className="text-sm text-irongrey font-medium">
                  {tuitionRates.length > 0 ? tuitionRates.map((rate, idx) => (
                    <tr key={rate.id} data-testid="public-apply-tuition-row" className={`${idx !== tuitionRates.length - 1 ? 'border-b border-gray-50' : ''} hover:bg-gray-50/50 transition-colors`}>
                      <td className="py-4 px-8">{rate.gradeLabel}</td>
                      <td className="py-4 px-8 text-right font-bold text-irongrey">{rate.amountLabel}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td className="py-4 px-8 text-irongrey/70" colSpan={2}>No tuition rates configured.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="bg-gray-50/30 p-4 border-t border-gray-100">
              <p className="text-[11px] text-irongrey/70 italic px-4">
                {isLoading ? 'Loading rates from settings...' : '* Yield reductions available for families with 3+ children.'}
              </p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer
        className="bg-white border-t border-gray-100 mt-auto py-10"
        data-extracted-from="owned:ystnj/public/home-footer"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col items-center md:items-start gap-1">
            <p className="text-xs font-semibold text-irongrey">© 2026 <a className="hover:text-primary transition-colors underline decoration-primary/30" href="https://intelliolabs.com" target="_blank" rel="noreferrer">Intellio, Inc.</a> All rights reserved.</p>
            <p className="text-[10px] text-irongrey/50 uppercase tracking-widest font-bold">Secure Education Management System</p>
          </div>
          <div className="flex items-center gap-8">
            <button 
              onClick={onNavigatePrivacy}
              className="text-xs font-medium text-irongrey/70 hover:text-[#237062] transition-colors"
            >
              Privacy Policy
            </button>
            <button 
              onClick={onNavigateTerms}
              className="text-xs font-medium text-irongrey/70 hover:text-[#237062] transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};
